// File: /js/shared/handlers/handleAjaxChange.js

import { buildContext }      from '/js/shared/context/requestContext.js';
import { ajaxAction }        from '/js/shared/ajax/ajaxAction.js';
import { sendFetchRequest }  from '/js/shared/utils/AjaxUtils.js';
import { update_html }       from '/js/shared/utils/DOMUtils.js';
import { handleAjaxError }   from '/js/shared/utils/ErrorUtils.js';
import { applyQuestionScoringModeUI, initMultipleChoice } from '/js/shared/quiz/multiple-choice-toggle.js';

function collectContestQuizUploadAnswerState() {
  const section = document.querySelector('.answer-blocks[data-block-name="answer_blocks"]');
  if (!section) return [];

  const blocks = Array.from(section.querySelectorAll('.blocks-container .block'));

  return blocks.map((block, index) => {
    const richtext = block.querySelector('.richtext');
    const checkbox = block.querySelector('.answer-correct-toggle input[type="checkbox"]');

    return {
      index,
      content: richtext?.innerHTML ?? '',
      is_correct: !!checkbox?.checked,
    };
  });
}

function filterModeChangeHtml(html) {
  if (!html || typeof html !== 'object') return {};

  const filtered = {};

  for (const [key, value] of Object.entries(html)) {
    // Structural rule:
    // never replace the whole answer block on mode switch.
    if (key.includes('answer_blocks')) continue;
    filtered[key] = value;
  }

  return filtered;
}

async function runResponseScripts(response) {
  if (!response?.script) return;

  const scripts = Array.isArray(response.script) ? response.script : [response.script];

  await Promise.all(
    scripts.map(src =>
      import(src)
        .then(mod => {
          if (typeof mod.setupAll === 'function') {
            mod.setupAll();
          }
        })
        .catch(err => console.error(`Erreur chargement script ${src}:`, err))
    )
  );
}

function handleQuestionScoringModeChange(data) {
  const renderMode = data.renderMode ?? data.render_mode ?? '';
  const nextMode   = data.new_value ?? '';

  // Upload mode: no persisted state yet.
  // The cleanest structural behavior is local-only UI mutation.
  if (renderMode === 'upload') {
    applyQuestionScoringModeUI(nextMode, document);
    initMultipleChoice(document);
    return;
  }

  // Edit mode: persist DB normalization server-side, but do NOT let the server
  // replace the whole answers block. Keep DOM stable and mutate only the toggles locally.
  sendFetchRequest({
    payload: data,
    isJson: true,
    onBefore: () => {
      document.body.classList.add('cursor-wait');
    },
    onSuccess: async (response) => {
      if (response.alert) alert(response.alert);

      if (response.redirect) {
        window.location.replace(response.redirect);
        return;
      }

      const safeHtml = filterModeChangeHtml(response.html);
      if (Object.keys(safeHtml).length > 0) {
        update_html(safeHtml);
      }

      if (response.urlChange) {
        history.replaceState(null, '', response.urlChange);
      }

      applyQuestionScoringModeUI(nextMode, document);
      initMultipleChoice(document);

      await runResponseScripts(response);
    },
    onError: (err, details) => {
      handleAjaxError(err, details);
    },
    onFinally: () => {
      document.body.classList.remove('cursor-wait');
    }
  });
}

export function handleAjaxChange(event) {
  const el =
    event.target.closest('[data-ajax-onchange="true"]') ||
    event.target.closest('[data-ajax-change="true"]');

  if (!el || !el.dataset.name) return;

  // Pass `el` to buildContext so the scope-hash override applies to
  // inputs nested inside an embedded editor (variant select on a social
  // card embedded in a quiz edit page, etc.).
  const data = {
    ...buildContext(el),
    ...el.dataset
  };

  const type = (el.type || '').toLowerCase();

  if (type === 'checkbox') {
    data.new_value = el.checked ? '1' : '0';
  } else if (type === 'radio') {
    data.new_value = el.value ?? '';
  } else {
    data.new_value = el.value ?? '';
  }

  if (el.name) {
    data.field = el.name;
    data[el.name] = data.new_value;
  }

  if (el.dataset.name === 'changeQuestionScoringMode') {
    if ((el.dataset.renderMode ?? '') === 'upload') {
      data.answer_state = JSON.stringify(collectContestQuizUploadAnswerState());
    }

    handleQuestionScoringModeChange(data);
    return;
  }

  ajaxAction(data);
}